import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ProFormColumnsType, BetaSchemaForm } from '@ant-design/pro-components';
import { Tag, Form } from 'antd';
import { IconBuilding, IconFileText } from '@tabler/icons-react';
import { CrudMode, FormItemValueType, renderListToTag } from '@ohpen/enso-ui';
import dayjs from 'dayjs';

import { FieldType, IntermediaryResponse, UserRoleType } from '@pmp/api';
import { TabProps } from '../interfaces';
import { CrudFormCard } from './CrudFormCard';

export const PersonalDetailsTab: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;

  const watchedRoles = Form.useWatch('roles', form);
  const watchedIrn = Form.useWatch('irn', form);
  const showFullName = (watchedRoles as UserRoleType[] | undefined)?.includes(UserRoleType.ADVISOR) && !!watchedIrn;

  const registrationColumns: ProFormColumnsType<IntermediaryResponse, 'text'>[] = useMemo(
    () => [
      {
        title: t('lender.brokers.labels.currentStatus'),
        dataIndex: 'userStatus',
        valueType: 'switch',
        readonly,
        fieldProps: readonly
          ? undefined
          : {
            checkedChildren: t('lender.brokers.labels.active'),
            unCheckedChildren: t('lender.brokers.labels.inactive') ?? t('lender.brokers.labels.inactive'),
          },
        render: (dom, entity) => {
          if (!readonly) return dom;
          const active = !!(entity as any)?.value;

          return (
            <Tag color={active ? 'green' : 'red'}>
              {active
                ? t('lender.brokers.labels.active')
                : (t('lender.brokers.labels.inactive') ?? t('lender.brokers.labels.inactive'))}
            </Tag>
          );
        },
        colProps: { span: 24 },
      },
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.roles'),
            dataIndex: 'roles',
            valueType: FormItemValueType.select,
            readonly,
            fieldProps: {
              mode: 'multiple',
              options: selectsOptions[FieldType.USER_ROLE_TYPE],
            },
            render: renderListToTag,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.irn'),
            dataIndex: 'irn',
            valueType: FormItemValueType.text,
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.brokers.labels.reference'),
            dataIndex: 'ohpenId',
            valueType: FormItemValueType.text,
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.fcaStatus'),
            dataIndex: 'fcaStatus',
            valueType: FormItemValueType.select,
            readonly,
            fieldProps: {
              options: selectsOptions[FieldType.USER_STATUS],
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.brokers.labels.firmName'),
            dataIndex: 'firmId',
            valueType: FormItemValueType.select,
            readonly,
            fieldProps: {
              options: selectsOptions[FieldType.FIRMS],
              showSearch: true,
              optionFilterProp: 'label',
            },
            formItemProps: {
              rules: [{ required: true }],
            },
            render: renderListToTag,
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [t, readonly, selectsOptions],
  );

  const personalColumns: ProFormColumnsType<IntermediaryResponse, 'text'>[] = useMemo(
    () => [
      {
        title: t('lender.brokers.labels.title'),
        dataIndex: ['personalData', 'title'],
        valueType: FormItemValueType.select,
        fieldProps: {
          options: [
            { label: t('lender.brokers.labels.mr'), value: 'mr' },
            { label: t('lender.brokers.labels.miss'), value: 'miss' },
            { label: t('lender.brokers.labels.mrs'), value: 'mrs' },
          ],
        },
        readonly,
        colProps: { span: 12 },
      },
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.firstName'),
            dataIndex: ['personalData', 'firstName'],
            valueType: FormItemValueType.text,
            readonly,
            colProps: { span: 12 },
          },
          {
            title: t('lender.brokers.labels.lastName'),
            dataIndex: ['personalData', 'lastName'],
            valueType: 'text',
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.fullName'),
            dataIndex: ['personalData', 'displayName'],
            valueType: FormItemValueType.text,
            readonly,
            hideInForm: !showFullName,
            colProps: { span: 12 },
          },
          {
            title: t('lender.brokers.labels.dateOfBirth'),
            dataIndex: ['personalData', 'birthDate'],
            valueType: FormItemValueType.date,
            readonly,
            formItemProps: {
              rules: [
                {
                  validator: (_: unknown, value: unknown) => {
                    if (!value) return Promise.resolve();
                    const age = dayjs().diff(dayjs(value as string), 'year');
                    if (age < 18) return Promise.reject(new Error(t('lender.brokers.validation.mustBeAtLeast18')));
                    if (age > 80) return Promise.reject(new Error(t('lender.brokers.validation.mustBeAtMost80')));
                    return Promise.resolve();
                  },
                },
              ],
            },
            colProps: { span: 12 },
          },
        ],
      },
      {
        valueType: FormItemValueType.group,
        columns: [
          {
            title: t('lender.brokers.labels.haveLivedOutsideOfTheUk'),
            dataIndex: ['personalData', 'haveLivedOutsideOfTheUk'],
            valueType: 'radio',
            readonly,
            fieldProps: {
              options: [
                { label: t('global.labels.yes'), value: true },
                { label: t('global.labels.no'), value: false },
              ],
            },
            formItemProps: {
              rules: [{ required: true }],
            },
            colProps: { span: 12 },
          },
          {
            title: t('lender.brokers.labels.previousName'),
            dataIndex: ['personalData', 'previousName'],
            valueType: FormItemValueType.text,
            readonly,
            colProps: { span: 12 },
          },
        ],
      },
    ],
    [readonly, t, showFullName],
  );

  return (
    <>
      <CrudFormCard icon={<IconBuilding />} title={t('lender.brokers.labels.registrationDetails')}>
        <BetaSchemaForm<IntermediaryResponse> form={form} layoutType="Embed" columns={registrationColumns} submitter={false} />
      </CrudFormCard>
      <CrudFormCard icon={<IconFileText />} title={t('lender.brokers.labels.personalDetails')}>
        <BetaSchemaForm<IntermediaryResponse> form={form} layoutType="Embed" columns={personalColumns} submitter={false} />
      </CrudFormCard>
    </>);
};
